import React, { useState, useEffect } from "react";
import axiosInstance from "../../services/auth";
import "./Invoice.css";
import { toast } from "react-toastify";

const InvoiceForm = ({
  selectedCompany,
  companyId,
  companyCode,
  invoice,
  onClose,
}) => {
  const [vesselCode, setVesselCode] = useState("");
  const [locations, setLocations] = useState([]);
  const [vessel, setVessel] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axiosInstance.get("/invoice/location");
        setLocations(response.data.data);
      } catch (error) {
        toast.error(error.message);
      }
    };
    fetchLocations();

    const fetchVessel = async () => {
      try {
        const response = await axiosInstance.get(
          `/invoice/vessal/${companyId}`,
        );
        setVessel(response.data.vessels);
      } catch (error) {
        toast.error("Error fetching invoice");
      }
    };
    fetchVessel();
  }, [companyId]);

  const [formData, setFormData] = useState(
    invoice
      ? {
          ...invoice,
          invoice_date: invoice.invoice_date
            ? new Date(invoice.invoice_date).toISOString().split("T")[0]
            : "",
          bill: invoice.bill || [],
          vesselSearch: invoice.project_code.vessel_name,
          locationSearch: invoice.supplier.yard_name,
        }
      : {
          company: companyId,
          job_no: "",
          project_code: "",
          trade: "piping",
          supplier: "",
          invoice_status: "pending",
          invoice_date: "",
          order_no: "",
          gst_rate: selectedCompany.gst_pct,
          company_code: "",
          bill: [
            {
              job_no: "",
              description: "",
              quantity: "",
              unit_price: "",
              amount: 0,
            },
          ],
          total_without_gst: 0,
          gst_amount: 0,
          total_with_gst: 0,
        },
  );

  const sanitizeFormData = (data) => {
    const sanitizedData = { ...data };
    delete sanitizedData.locationSearch;
    delete sanitizedData.showLocationDropdown;
    delete sanitizedData.showVesselDropdown;
    delete sanitizedData.vesselSearch;
    return sanitizedData;
  };

  const handleChangeCompanyCode = () => {
    const updatedCompanyCode = `${companyCode}-${vesselCode || "NA"}`;
    setFormData({
      ...formData,
      company_code: updatedCompanyCode,
    });
  };

  useEffect(() => {
    handleChangeCompanyCode();
  }, [companyCode, vesselCode]);

  const handleOutsideClick = (e) => {
    if (e.target.classList.contains("modal")) {
      onClose();
    }
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const gst = selectedCompany.gst_pct * 0.01;

  const calculateTotals = (updatedBill = formData.bill) => {
    const totalWithoutGST = updatedBill.reduce(
      (acc, item) => acc + parseFloat(item.amount || 0),
      0,
    );
    const gstAmount = totalWithoutGST * gst;
    const totalWithGST = totalWithoutGST + gstAmount;

    setFormData({
      ...formData,
      total_without_gst: totalWithoutGST,
      gst_amount: gstAmount,
      total_with_gst: totalWithGST,
    });
  };

  const handleChange = (e, index) => {
    const { name, value } = e.target;

    if (name.startsWith("bill")) {
      const [_, idx, field] = name.split(".");
      const updatedBill = [...formData.bill];
      updatedBill[parseInt(idx)] = {
        ...updatedBill[parseInt(idx)],
        [field]: value,
      };

      if (field === "quantity" || field === "unit_price") {
        const quantity = parseFloat(updatedBill[parseInt(idx)].quantity || 0);
        const unitPrice = parseFloat(
          updatedBill[parseInt(idx)].unit_price || 0,
        );
        updatedBill[parseInt(idx)].amount = quantity * unitPrice;
      }

      setFormData({ ...formData, bill: updatedBill });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleAddBillRow = () => {
    const newBillRow = {
      job_no: "",
      description: "",
      quantity: 0,
      unit_price: 0,
      amount: 0,
    };
    const updatedBill = [...formData.bill, newBillRow];
    setFormData({ ...formData, bill: updatedBill });
  };

  // Insert a new row above the current index.
  const handleInsertBillRow = (index) => {
    const newBillRow = {
      job_no: "",
      description: "",
      quantity: 0,
      unit_price: 0,
      amount: 0,
    };
    const updatedBill = [...formData.bill];
    updatedBill.splice(index, 0, newBillRow);
    setFormData({ ...formData, bill: updatedBill });
  };

  const handleRemoveBillRow = (index) => {
    const updatedBill = formData.bill.filter((_, i) => i !== index);
    setFormData({ ...formData, bill: updatedBill });
  };

  useEffect(() => {
    if (formData.bill.length > 0) {
      calculateTotals(formData.bill);
    }
  }, [formData.bill]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedCompany?.status === false) {
      toast.error("The company is not active");
      return;
    }
    if (!formData.supplier) {
      toast.error("Please select a valid supplier");
      return;
    }

    setLoading(true);
    const sanitizedData = sanitizeFormData(formData);

    try {
      if (invoice) {
        const response = await axiosInstance.patch(
          `/invoice/${invoice._id}`,
          sanitizedData,
        );
        if (response.data.statusCode === 400) {
          throw { response: { data: { message: response.data.message[0] } } };
        }
      } else {
        const response = await axiosInstance.post("/invoice", sanitizedData);
        if (response.data.statusCode === 400) {
          throw { response: { data: { message: response.data.message[0] } } };
        }
      }
      onClose();
      toast.success(
        invoice
          ? "Invoice updated successfully"
          : "Invoice created successfully",
      );
    } catch (error) {
      // console.error("Error saving invoice:", error);
      let errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Error saving invoice";
      if (Array.isArray(errorMessage)) {
        errorMessage = errorMessage[0];
      }
      if (errorMessage === "User cannot edit this invoice") {
        toast.error("You do not have permission to edit this invoice.");
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const authData =
    JSON.parse(localStorage.getItem("authData")) ||
    JSON.parse(sessionStorage.getItem("authData"));
  const userRole = authData.role;

  useEffect(() => {
    if (formData.bill.length > 0) {
      calculateTotals(formData.bill);
    }
  }, [formData.bill]);

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      // onClick={handleOutsideClick}
      style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
    >
      <div
        className="modal-dialog modal-xl modal-dialog-centered"
        role="document"
      >
        <div className="modal-content invoice_model">
          <div className="modal-header text-white text-center vesel_form">
            <h5 className="modal-title">
              {invoice ? "Edit Invoice" : "Create Invoice"}
            </h5>
            <button
              type="button"
              className="btn-close text-white"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body vesel_form">
            <form onSubmit={handleSubmit}>
              {/* Invoice Details Section */}
              <div className="card shadow-sm mb-4 rounded-3">
                <div className="card-body">
                  <h5 className="card-title text-center mb-4">
                    Invoice Details
                  </h5>
                  <div className="row">
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Job No</label>
                      <input
                        type="text"
                        className="form-control"
                        name="job_no"
                        value={formData.job_no}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-4 mb-3  position-relative">
                      <label className="form-label">Project Code</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Type vessel name or code"
                        value={
                          vessel.find(
                            (item) => item._id === formData.project_code,
                          )?.vessel_name ||
                          formData.vesselSearch ||
                          ""
                        }
                        onChange={(e) => {
                          const searchValue = e.target.value;
                          setFormData({
                            ...formData,
                            vesselSearch: searchValue,
                            project_code: "",
                          });
                        }}
                        onFocus={() =>
                          setFormData({ ...formData, showVesselDropdown: true })
                        }
                        required
                      />
                      {formData.showVesselDropdown && (
                        <ul
                          className="list-group position-absolute"
                          style={{
                            zIndex: 1200,
                            width: "100%",
                            maxHeight: 220,
                            overflowY: "auto",
                          }}
                        >
                          {vessel
                            .filter(
                              (item) =>
                                item.vessel_name
                                  .toLowerCase()
                                  .includes(
                                    formData.vesselSearch?.toLowerCase() || "",
                                  ) ||
                                item.vessel_code
                                  .toLowerCase()
                                  .includes(
                                    formData.vesselSearch?.toLowerCase() || "",
                                  ),
                            )
                            .map((item) => (
                              <li
                                key={item._id}
                                className="list-group-item list-group-item-action"
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  setFormData({
                                    ...formData,
                                    project_code: item._id,
                                    vesselSearch: "",
                                    showVesselDropdown: false,
                                  });
                                  setVesselCode(item.vessel_code);
                                }}
                              >
                                {`${item.vessel_name} - ${item.vessel_code}`}
                              </li>
                            ))}
                          {vessel.filter(
                            (item) =>
                              item.vessel_name
                                .toLowerCase()
                                .includes(
                                  formData.vesselSearch?.toLowerCase() || "",
                                ) ||
                              item.vessel_code
                                .toLowerCase()
                                .includes(
                                  formData.vesselSearch?.toLowerCase() || "",
                                ),
                          ).length === 0 && (
                            <li className="list-group-item list-group-item-action">
                              No results found
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Work Order No</label>
                      <input
                        type="text"
                        className="form-control"
                        name="order_no"
                        value={formData.order_no}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-2 mb-3">
                      <label className="form-label">Company Code</label>
                      <input
                        type="text"
                        className="form-control"
                        name="company_code"
                        value={
                          formData.company_code ||
                          `${companyCode}-${vesselCode || "NA"}`
                        }
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Trade</label>
                      <select
                        className="form-select"
                        name="trade"
                        value={formData.trade}
                        onChange={handleChange}
                        required
                      >
                        <option value="piping">Piping</option>
                        <option value="mechanical">Mechanical</option>
                      </select>
                    </div>
                    <div className="col-md-4 mb-3  position-relative">
                      <label className="form-label">Supplier</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Type location name or code"
                        value={
                          locations.find(
                            (item) => item._id === formData.supplier,
                          )?.yard_name ||
                          formData.locationSearch ||
                          ""
                        }
                        onChange={(e) => {
                          const searchValue = e.target.value;
                          setFormData({
                            ...formData,
                            locationSearch: searchValue,
                            supplier: "",
                          });
                        }}
                        onFocus={() =>
                          setFormData({
                            ...formData,
                            showLocationDropdown: true,
                          })
                        }
                        required
                      />
                      {formData.showLocationDropdown && (
                        <ul
                          className="list-group position-absolute"
                          style={{
                            zIndex: 1200,
                            width: "100%",
                            maxHeight: 220,
                            overflowY: "auto",
                          }}
                        >
                          {locations
                            .filter(
                              (item) =>
                                item.yard_name
                                  .toLowerCase()
                                  .includes(
                                    formData.locationSearch?.toLowerCase() ||
                                      "",
                                  ) ||
                                item.code
                                  .toLowerCase()
                                  .includes(
                                    formData.locationSearch?.toLowerCase() ||
                                      "",
                                  ),
                            )
                            .map((item) => (
                              <li
                                key={item._id}
                                className="list-group-item list-group-item-action"
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  setFormData({
                                    ...formData,
                                    supplier: item._id,
                                    locationSearch: "",
                                    showLocationDropdown: false,
                                  });
                                }}
                              >
                                {`${item.yard_name} - ${item.code}`}
                              </li>
                            ))}
                          {locations.filter(
                            (item) =>
                              item.yard_name
                                .toLowerCase()
                                .includes(
                                  formData.locationSearch?.toLowerCase() || "",
                                ) ||
                              item.code
                                .toLowerCase()
                                .includes(
                                  formData.locationSearch?.toLowerCase() || "",
                                ),
                          ).length === 0 && (
                            <li className="list-group-item list-group-item-action">
                              No results found
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Invoice Status</label>
                      <select
                        className="form-select"
                        name="invoice_status"
                        value={formData.invoice_status}
                        onChange={handleChange}
                        required
                      >
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="submitted">Submitted</option>
                      </select>
                    </div>
                    <div className="col-md-2 mb-3">
                      <label className="form-label">Invoice Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="invoice_date"
                        value={formData.invoice_date}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="row"></div>
                </div>
              </div>

              {/* Bill Items Section */}
              <div
                className="card shadow-sm mb-4 rounded-3"
                style={{ zIndex: 0 }}
              >
                <div className="card-body">
                  <h5 className="card-title text-center mb-4">Bill Items</h5>
                  {formData.bill &&
                    Array.isArray(formData.bill) &&
                    formData.bill.map((item, index) => (
                      <div key={index} className="row align-items-center mb-2">
                        {/* Job No with serial number embedded in label */}
                        <div className="col-md-2">
                          <label className="form-label">{`${
                            index + 1
                          }. Job No`}</label>
                          <input
                            type="text"
                            className="form-control"
                            name={`bill.${index}.job_no`}
                            value={item.job_no}
                            onChange={(e) => handleChange(e, index)}
                            required
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Description</label>
                          <textarea
                            className="form-control custom-textarea"
                            name={`bill.${index}.description`}
                            value={item.description}
                            onChange={(e) => handleChange(e, index)}
                            rows="2"
                            required
                          />
                        </div>
                        <div className="col-md-2">
                          <label className="form-label">Quantity</label>
                          <input
                            type="number"
                            className="form-control"
                            name={`bill.${index}.quantity`}
                            value={item.quantity === 0 ? "" : item.quantity}
                            onChange={(e) => {
                              const value =
                                e.target.value === ""
                                  ? ""
                                  : Math.max(0, e.target.value);
                              handleChange(
                                {
                                  target: {
                                    name: `bill.${index}.quantity`,
                                    value,
                                  },
                                },
                                index,
                              );
                            }}
                            required
                          />
                        </div>
                        <div className="col-md-2">
                          <label className="form-label">Unit Price</label>
                          <input
                            type="number"
                            className="form-control"
                            name={`bill.${index}.unit_price`}
                            value={item.unit_price === 0 ? "" : item.unit_price}
                            onChange={(e) => {
                              const value = Math.max(0, e.target.value);
                              handleChange(
                                {
                                  target: {
                                    name: `bill.${index}.unit_price`,
                                    value,
                                  },
                                },
                                index,
                              );
                            }}
                            required
                          />
                        </div>
                        <div className="col-md-2">
                          <label className="form-label">Amount</label>
                          <input
                            type="number"
                            className="form-control"
                            name={`bill.${index}.amount`}
                            value={item.amount}
                            readOnly
                          />
                        </div>
                        <div className="col-md-1 d-flex align-items-center justify-content-end mt-4">
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm me-1 "
                            onClick={() => handleInsertBillRow(index)}
                          >
                            <i className="bi bi-arrow-up-square"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleRemoveBillRow(index)}
                          >
                            <i className="bi bi-trash3"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={handleAddBillRow}
                  >
                    <i className="bi bi-plus-circle me-2"></i>
                    Add
                  </button>
                </div>
              </div>

              {/* Total Section */}
              <div className="row">
                <div className="col-md-4">
                  <label className="form-label">Total (Without GST)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="total_without_gst"
                    value={formData.total_without_gst.toFixed(2)}
                    readOnly
                  />
                </div>
                {["admin", "superadmin"].includes(userRole) && (
                  <>
                    <div className="col-md-4">
                      <label className="form-label">
                        GST Amount: {selectedCompany.gst_pct}%
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        name="gst_amount"
                        value={formData.gst_amount.toFixed(2)}
                        readOnly
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Total (With GST)</label>
                      <input
                        type="number"
                        className="form-control"
                        name="total_with_gst"
                        value={formData.total_with_gst.toFixed(2)}
                        readOnly
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="mt-4 text-center">
                <button type="submit" className="button-2" disabled={loading}>
                  {loading
                    ? invoice
                      ? "Updating"
                      : "Creating"
                    : invoice
                      ? "Update Invoice"
                      : "Create Invoice"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceForm;
